import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { SubmitFlow } from './submit-flow';

const DESKTOP_QUERY = '(min-width: 768px)';

interface SubmitModalCtx {
  openModal: () => void;
}

const Ctx = createContext<SubmitModalCtx | null>(null);

function isDesktop(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches;
}

/**
 * Provides the desktop "List your event" modal. On desktop, buttons open the
 * modal (same flow as /submit). On mobile the buttons navigate to /submit
 * instead — the provider only ever opens the modal on desktop, and <SubmitLink>
 * leaves the real <a href="/submit"> intact for mobile + no-JS.
 */
export function SubmitModalProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const openModal = useCallback(() => setOpen(true), []);
  return (
    <Ctx.Provider value={{ openModal }}>
      {children}
      {open && <SubmitModal onClose={() => setOpen(false)} />}
    </Ctx.Provider>
  );
}

export function useSubmitModal(): SubmitModalCtx | null {
  return useContext(Ctx);
}

/**
 * A "List your event" link. Real <a href="/submit"> so it works without JS and
 * on mobile (navigates). On desktop, JS intercepts and opens the modal instead.
 */
export function SubmitLink({
  className,
  children,
  onNavigate,
}: {
  className?: string;
  children: React.ReactNode;
  onNavigate?: () => void;
}) {
  const modal = useSubmitModal();
  return (
    <Link
      to="/submit"
      className={className}
      onClick={(e) => {
        if (modal && isDesktop()) {
          e.preventDefault();
          modal.openModal();
        }
        onNavigate?.();
      }}
    >
      {children}
    </Link>
  );
}

function SubmitModal({ onClose }: { onClose: () => void }) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Lock body scroll while the modal is open; restore on close.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab') trapFocus(e, dialogRef.current);
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={backdropRef}
      className="modal-backdrop submit-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === backdropRef.current) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="modal card submit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="submit-modal-title"
      >
        <div className="submit-modal-head">
          <div>
            <span className="eyebrow">Organizers</span>
            <h2 id="submit-modal-title" className="submit-modal-title">
              List your event — free
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="ai-orb-close submit-modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div ref={bodyRef} className="submit-modal-body stack">
          <SubmitFlow
            showMasthead={false}
            onSubmitted={() => bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
          />
        </div>
      </div>
    </div>
  );
}

/** Minimal focus trap for the dialog. */
function trapFocus(e: KeyboardEvent, container: HTMLElement | null) {
  if (!container) return;
  const focusable = container.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])'
  );
  if (focusable.length === 0) return;
  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}
